import { isCapacitor } from "./capacitor";
import { getFcmPermissionStatus } from "./fcm";
import { nativePlugin } from "./nativeplugin";

/*
 * Alles wat bepaalt of een melding je telefoon haalt, op één plek.
 *
 * Er zijn twee sloten, en ze staan los van elkaar:
 *
 *   1. De meldingspermissie. Android 13 en nieuwer vraagt hierom; zonder is er
 *      niets te tonen, hoe goed de rest ook werkt.
 *   2. Batterijbeheer. De app mag melden, maar het toestel houdt hem zo diep
 *      in slaap dat het uren duurt. Samsung, Xiaomi en OnePlus zijn hier
 *      strenger dan Android zelf.
 *
 * Tot nu toe vroeg de app alleen naar het eerste, en dan nog alleen als je het
 * knopje in de statusbalk zelf vond.
 */

export type BatterijStand = {
  /** Kent dit toestel batterijbeheer? Onder Android 6 niet. */
  supported: boolean;
  /** Staat Swift er al buiten? */
  ignoring: boolean;
};

interface SwiftBatteryPlugin {
  isIgnoring(): Promise<BatterijStand>;
  request(): Promise<{ opened: boolean }>;
  openAppSettings(): Promise<{ opened: boolean }>;
}

function batterijPlugin(): SwiftBatteryPlugin | null {
  if (!isCapacitor()) return null;
  return nativePlugin<SwiftBatteryPlugin>("SwiftBattery");
}

/** Buiten de Android-app bestaat batterijbeheer niet; dan is er niets te
 *  vragen en doen we alsof alles in orde is. */
export async function batterijStand(): Promise<BatterijStand> {
  if (!isCapacitor()) return { supported: false, ignoring: true };
  try {
    const p = batterijPlugin();
    if (!p) return { supported: false, ignoring: true };
    return await p.isIgnoring();
  } catch {
    /* Oudere APK zonder deze plugin: niet zeuren over iets wat je niet kunt
       vragen. */
    return { supported: false, ignoring: true };
  }
}

export async function vraagBatterijVrijstelling(): Promise<boolean> {
  if (!isCapacitor()) return false;
  try {
    const p = batterijPlugin();
    if (!p) return false;
    const r = await p.request();
    return r.opened;
  } catch {
    return false;
  }
}

export async function openAppInstellingen(): Promise<boolean> {
  if (!isCapacitor()) return false;
  try {
    const p = batterijPlugin();
    if (!p) return false;
    const r = await p.openAppSettings();
    return r.opened;
  } catch {
    return false;
  }
}

export type MeldingStand = {
  /** "granted" | "denied" | "prompt" | "default" | "unsupported" | "onbekend" */
  permissie: string;
  /** Waarom de permissie niet te lezen was, of null als dat wel lukte. */
  permissieFout: string | null;
  /** null = niet te lezen. */
  batterij: BatterijStand | null;
  batterijFout: string | null;
};

/*
 * Wachten, maar niet eeuwig.
 *
 * Een aanroep naar de Android-kant loopt via de Capacitor-brug, en die brug
 * belooft niet dat er ooit antwoord komt. Blijft het stil, dan blijft de
 * bijbehorende belofte hangen - en een try/catch redt je daar niet van, want er
 * wordt niets gegooid. Op 07-09-2026 stond het meldingenscherm daardoor
 * eindeloos op "Laden...".
 *
 * Vandaar een tijdslimiet op elke aanroep, en een uitkomst die ook "ik weet het
 * niet" kan zijn. Niet weten is een prima antwoord; niets zeggen niet.
 */
type Uitkomst<T> = { ok: true; waarde: T } | { ok: false; fout: string };

export async function metTijdslimiet<T>(
  wat: string,
  maak: () => Promise<T>,
  ms = 5000,
): Promise<Uitkomst<T>> {
  let tijd: ReturnType<typeof setTimeout> | undefined;
  try {
    const waarde = await Promise.race([
      maak(),
      new Promise<never>((_, weiger) => {
        tijd = setTimeout(() => weiger(new Error(`${wat}: geen antwoord binnen ${ms / 1000}s`)), ms);
      }),
    ]);
    return { ok: true, waarde };
  } catch (e) {
    return { ok: false, fout: e instanceof Error ? e.message : `${wat}: ${String(e)}` };
  } finally {
    if (tijd !== undefined) clearTimeout(tijd);
  }
}

/**
 * De permissie, ongeacht platform.
 *
 * getFcmPermissionStatus() geeft buiten de Android-app altijd "prompt" terug -
 * dat is daar een sein aan de aanroeper om de Web-Push-tak te nemen, geen echte
 * stand. In de browser is Notification.permission wel de waarheid, en zonder
 * dat onderscheid meldt dit scherm "nog niet gevraagd" terwijl je ze allang
 * hebt aanstaan.
 */
async function permissieStand(): Promise<string> {
  if (isCapacitor()) return getFcmPermissionStatus();
  if (typeof Notification === "undefined") return "unsupported";
  return Notification.permission;
}

/** Beide sloten in één keer, voor de banner en het instellingenscherm. Deze
 *  functie kan niet mislukken en kan niet blijven hangen: in het slechtste
 *  geval weet hij niets, en zegt hij dat. */
export async function meldingStand(): Promise<MeldingStand> {
  const [p, b] = await Promise.all([
    metTijdslimiet("permissie", permissieStand),
    metTijdslimiet("batterijbeheer", ruweBatterijStand),
  ]);
  return {
    permissie: p.ok ? p.waarde : "onbekend",
    permissieFout: p.ok ? null : p.fout,
    batterij: b.ok ? b.waarde : null,
    batterijFout: b.ok ? null : b.fout,
  };
}

/** De ongefilterde variant: hier mag een fout wél naar buiten, zodat
 *  meldingStand() kan vertellen wát er misging. */
async function ruweBatterijStand(): Promise<BatterijStand> {
  if (!isCapacitor()) return { supported: false, ignoring: true };
  const p = batterijPlugin();
  if (!p) return { supported: false, ignoring: true };
  return p.isIgnoring();
}

/*
 * Onthouden dat we het al een keer uit onszelf hebben gevraagd. Android toont
 * zijn permissiedialoog toch maar een paar keer, en een app die bij elke start
 * opnieuw begint te vragen is precies het soort app dat mensen wegdrukken
 * zonder te lezen.
 */
const GEVRAAGD = "swift_meldingen_gevraagd";

export function alGevraagd(): boolean {
  try {
    return localStorage.getItem(GEVRAAGD) === "1";
  } catch {
    return false;
  }
}

export function markeerGevraagd(): void {
  try {
    localStorage.setItem(GEVRAAGD, "1");
  } catch {
    /* Privémodus of geblokkeerde opslag: dan vragen we het nog een keer.
       Vervelend, niet stuk. */
  }
}

/*
 * De banner wegklikken. Niet voorgoed - wie hem wegdrukt heeft meestal geen zin
 * in dit moment, niet in dit onderwerp - maar wel lang genoeg om niet te gaan
 * zeuren. Via de instellingen is het altijd alsnog aan te zetten.
 */
const WEGGEKLIKT = "swift_meldingen_weggeklikt";
const RUSTPERIODE_DAGEN = 14;

export function bannerOnderdrukt(): boolean {
  try {
    const tot = Number(localStorage.getItem(WEGGEKLIKT) ?? "0");
    return Number.isFinite(tot) && Date.now() < tot;
  } catch {
    return false;
  }
}

export function onderdrukBanner(): void {
  try {
    const tot = Date.now() + RUSTPERIODE_DAGEN * 24 * 60 * 60 * 1000;
    localStorage.setItem(WEGGEKLIKT, String(tot));
  } catch {
    /* zie hierboven */
  }
}
